import { z } from "zod";

import dayjs, { Dayjs } from "../../../config/dayjs";
import { claimType, claimStatus } from "../../../constant/enum";
import { IUserResponse } from "../../user/type/user";

// Custom Zod transformer for Dayjs to Date conversion
const dayjsToDate = z
  .union([
    z.date(),
    z.custom<Dayjs>((val) => dayjs.isDayjs(val), {
      message: "Expected a valid date",
    }),
  ])
  .transform((val) => {
    if (dayjs.isDayjs(val)) {
      return val.toDate();
    }
    return val;
  });

// Base claim fields that are common to both request and response
const BaseClaimSchema = z.object({
  claimType: z.enum(claimType as [string, ...string[]]),
  startDate: dayjsToDate.optional(),
  endDate: dayjsToDate.optional(),
  claimAmount: z.number().positive().optional(),
  claimDetails: z.string().optional(),
  status: z
    .enum(claimStatus as [string, ...string[]])
    .default("pending")
    .optional(),
});

// Schema for creating/updating claims
export const ClaimRequestSchema = BaseClaimSchema.extend({
  totalDays: z.number().positive().optional(),
  fileUrl: z.array(z.string().url()).optional(),
  removedFiles: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  // startDate and endDate are required for OT and travel claim types
  if (
    (data.claimType === "normalOT" ||
      data.claimType === "weekendOT" ||
      data.claimType === "travel" ||
      data.claimType === "attendClient") &&
    !data.startDate
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["startDate"],
    });
  }

  if (
    (data.claimType === "normalOT" ||
      data.claimType === "weekendOT" ||
      data.claimType === "travel" ||
      data.claimType === "attendClient") &&
    !data.endDate
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["endDate"],
    });
  }

  // claimAmount is required for non-OT claim types
  if (
    data.claimType !== "normalOT" &&
    data.claimType !== "weekendOT" &&
    data.claimType !== "attendClient" &&
    (data.claimAmount === undefined || data.claimAmount === null)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["claimAmount"],
    });
  }
});

const UpdateClaimStatusRequestSchema = z.object({
  status: z.enum(claimStatus as [string, ...string[]]).optional(),
});

// Schema for claim responses from API
export const ClaimResponseSchema = BaseClaimSchema.extend({
  id: z.string(),
  user: z.custom<IUserResponse>(),
  totalDays: z.number().positive().optional(),
  fileUrl: z.array(z.string().url()).optional(),
  approvedByUser: z.custom<IUserResponse>().optional(),
  approvedAt: z.date().optional(),
  rejectedByUser: z.custom<IUserResponse>().optional(),
  rejectedAt: z.date().optional(),
  rejectedReason: z.string().optional(),
  archive: z.date().optional(),
});

export type IClaimRequest = z.infer<typeof ClaimRequestSchema>;
export type IClaimResponse = z.infer<typeof ClaimResponseSchema>;
export type IUpdateClaimStatusRequest = z.infer<
  typeof UpdateClaimStatusRequestSchema
>;

export interface IApiResponseGetClaims {
  results: IClaimResponse[];
  page: string;
  limit: number;
  totalPages: number;
  totalResults: number;
}
